import _ from 'lodash';
import {
  Box,
  Divider,
  IconButton,
} from '@material-ui/core';
import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../../../context/language/language';
import { getImageURL } from '../../../../utils/getImageURL';
import useResponsive from '../../../../utils/responsive';
import { Paragraph, Space, Subheading } from '../../../common';
import { defaultModalSize } from '../modalSize';

const BackIcon = getImageURL('/icon/back-button.svg');
const CloseIcon = getImageURL('/icon/close-button.svg');
const LeadingIcon = getImageURL('/icon/leading.svg');

const Row = styled.div(({ isTitleCenter }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  ...isTitleCenter && {
    marginLeft: 'auto',
    marginRight: 'auto'
  }
}));

const Column = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const Header = styled(Row)(({ theme, isShowBorderBottom }) => ({
  height: 92,
  padding: '0 10px',

  ...theme.isTablet && {
    padding: '6px 24px 0',
  },

  ...theme.isDesktopOrLaptop && {
    padding: '0 20px',
  },

  ...theme.isMobile && {
    height: 36,
    padding: isShowBorderBottom ? '24px 12px 8px' : '24px 0',
    borderBottom: isShowBorderBottom ? `1px solid ${theme.colors.grey100}` : 'none',
    margin: isShowBorderBottom ? '0 -12px' : 'initial'
    
  }
}));

const Container = styled.div(({ theme, isDefaultModalSize, paddingTop, containerHeight, paddingBottom }) => ({
  padding: 0,
  ...isDefaultModalSize && {
    overflowY: 'auto',
  },

  ...theme.isTablet && {
    paddingTop,

    ...isDefaultModalSize && {
      height: containerHeight || defaultModalSize.tablet.contentHeight,
    },
  },

  ...theme.isDesktopOrLaptop && {
    padding: `${paddingTop}px 24px ${paddingBottom}px`,
    ...isDefaultModalSize && {
      height: containerHeight || defaultModalSize.desktop.contentHeight,
    },
  },

  '-ms-overflow-style': 'none',
  'scrollbar-width': 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));

const CustomImg = styled.img(({ theme, width = 44, height = 44 }) => ({
  width,
  height,

  ...theme.isMobile && {
    width: width - 8,
    height: height - 8
  },
}));

const InfoImg = styled.img(({ theme, width = 24, height = 24 }) => ({
  width,
  height,
}));

const CustomTouchableOpacity = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  padding: 0,

  ...theme.isMobile && {
    width: 36,
    height: 36
  },
}));

const Description = styled(Paragraph)(({ theme }) => ({
  color: theme.colors.grey500,
  marginLeft: theme.isTabletOrMobile ? 0 : 10,
  paddingBottom: theme.isTabletOrMobile ? 10 : 0,
}));

const Footer = styled(Row)(({ theme }) => ({
  justifyContent: 'center',
  padding: '12px 0 24px',
}));

const CustomSubheading = styled(Subheading)(({ theme, isTitleCenter }) => ({
  ...isTitleCenter && {
    ...theme.fonts.header3
  },
  ...theme.isTabletOrMobile && {
    color: theme.colors.grey700,
  },

}));

const InfoAddressContainer = styled.div(({ theme }) => ({
  height: 'auto',
  backgroundColor: '#FCF2D3',
  padding: 12,
  ...theme.isMobile && {
    ...theme.radius.m
  },
}));

const InfoMessage = styled.div(({ theme }) => ({
  ...theme.fonts.textM,
  color: '#424242'
}));

export const Main = styled.div(({
  theme,
  width,
  height,
  noPaddingX
}) => ({
  width,
  height,
  overflow: 'hidden',

  ...theme.isTablet && {
    padding: '0 24px 24px',
  },

  ...theme.isDesktopOrLaptop && {
    padding: 0,
  },

  ...(noPaddingX && theme.isMobile) && {
    padding: 0,
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
  },

  '&::-webkit-scrollbar': {
    width: 4,
    backgroundColor: '#FFFFFF',
  },

  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: '#E8E8E8',
  },
}));

export default function Layout({
  containerRef,
  children,
  title,
  description = '',
  backButtonShown = true,
  onBack = () => {},
  onClose = () => false,
  width = defaultModalSize.desktop.width,
  height = defaultModalSize.desktop.height,
  headerSpace = 0,
  bottomSpace = 0,
  storeCloseWarningDesktop,
  storeCloseWarningMobile,
  isDefaultModalSize = true,
  scrollY = true,
  containerId = 'layout-container',
  footer,
  isShowBorderBottom = false,
  isTitleCenter = false,
  paddingTop = 20,
  infoAddress = false,
  containerHeight,
  containerOverflowY,
  maxHeight = 'none',
  paddingBottom = 24
}) {
  const {
    isTabletOrMobile, isTablet, isDesktopOrLaptop, isMobile
  } = useResponsive();
  const word = useLanguage();

  const getContainerOverflow = () => {
    if (containerOverflowY) {
      return containerOverflowY;
    }
    return scrollY ? 'auto' : 'hidden';
  };

  const getHeight = (h) => {
    if (isMobile) {
      return '100%';
    }
    return h;
  };

  return (
    <div
      id="modal-layout"
      style={{
        overflow: 'hidden',
        width: isMobile ? '100%' : width,
        height: getHeight(height),
        maxHeight,
        paddingBottom: bottomSpace,
      }}
    >
      <Header isShowBorderBottom={isShowBorderBottom}>
        {!isTabletOrMobile && (
          <Row isTitleCenter={isTitleCenter}>
            {backButtonShown && (
              <CustomTouchableOpacity
                id="back-quick"
                data-cta-name-en="Back"
                data-cta-name-th="กลับ"
                data-cta-type="Button"
                onClick={() => onBack()}
              >
                <CustomImg src={BackIcon} alt="back" />
              </CustomTouchableOpacity>
            )}
            <Space line={0.8} />
            <Column>
              <Row isTitleCenter={isTitleCenter}>
                <CustomSubheading isTitleCenter={isTitleCenter}>{word(title)}</CustomSubheading>
                <Description>{description}</Description>
              </Row>
              {storeCloseWarningDesktop && (
                <Row>
                  {storeCloseWarningDesktop}
                </Row>
              )}
            </Column>

          </Row>
        )}

        {isTabletOrMobile && (
          <Row>
            {backButtonShown && (
              <CustomTouchableOpacity
                id="back-quick"
                data-cta-name-en="Back"
                data-cta-name-th="กลับ"
                data-cta-type="Button"
                onClick={() => onBack()}
              >
                <CustomImg src={BackIcon} alt="back" />
              </CustomTouchableOpacity>
            )}
            {backButtonShown && <Space line={0.8} />}
            <Column>
              <Row>
                <CustomSubheading>{word(title)}</CustomSubheading>
                {storeCloseWarningMobile && (
                <Box>
                  {storeCloseWarningMobile}
                </Box>
                )}
              </Row>

              {description && <Description>{description}</Description>}
            </Column>
          </Row>
        )}
        {onClose && (
          <CustomTouchableOpacity
            id="close-quick"
            data-cta-name-en="Close"
            data-cta-name-th="ปิด"
            data-cta-type="Button"
            onClick={() => onClose()}
          >
            <CustomImg src={CloseIcon} alt="close" />
          </CustomTouchableOpacity>
        )}
      </Header>
      {(isDesktopOrLaptop || isTablet) && (
        <Divider />
      )}
      {infoAddress && (
        <InfoAddressContainer>
          <div style={{ display: 'flex' }}>
            <div style={{ paddingRight: 6 }}>
              <InfoImg src={LeadingIcon} width={24} height={24} />
            </div>
            <InfoMessage>
              {`${infoAddress}`}
            </InfoMessage>
          </div>
        </InfoAddressContainer>
      )}
      <Container
        id={containerId}
        ref={containerRef}
        isDefaultModalSize={isDefaultModalSize}
        style={{ overflowY: getContainerOverflow() }}
        paddingTop={paddingTop}
        paddingBottom={paddingBottom}
        containerHeight={containerHeight}
      >
        {children}
      </Container>
      {!_.isEmpty(footer) && (
        <Footer>
          {footer}
        </Footer>
      )}
    </div>
  );
}

const ChangeDeliveryFlowHeading = styled.div(({ theme }) => ({
  color: theme.colors.grey700,
  ...theme.fonts.header4,

  ...!theme.isMobile && {
    textAlign: 'center',
    ...theme.fonts.header3,
  },
}));

const ChangeDeliveryFlowDescription = styled.div(({ theme }) => ({
  textAlign: 'center',
  fontSize: theme.fonts.textM.fontSize,
  lineHeight: theme.fonts.textM.lineHeight,
  fontWeight: 400,
  color: theme.colors.grey550,
}));

export function CustomLayoutChangeDeliveryFlow({
  children,
  title,
  description = '',
  backButtonShown = true,
  onBack = () => {},
  onClose = () => false,
  width = 752,
  headerSpace = 0,
  bottomSpace = 0,
  isDefaultModalSize = false
}) {
  const {
    isDesktopOrLaptop,
    isMobile,
    isTablet,
    isTabletOrMobile,
  } = useResponsive();
  const word = useLanguage();
  return (
    <div
      style={{
        width: isMobile ? '100%' : width,
      }}
    >
      <Header>
        {backButtonShown && (
        <Row>
          <CustomTouchableOpacity
            id="back-quick"
            data-cta-name-en="Back"
            data-cta-name-th="กลับ"
            data-cta-type="Button"
            onClick={() => onBack()}
          >
            <CustomImg src={BackIcon} alt="back" />
          </CustomTouchableOpacity>
        </Row>
        )}
        <Row>
          <ChangeDeliveryFlowHeading>{word(title)}</ChangeDeliveryFlowHeading>
          <ChangeDeliveryFlowDescription>{description}</ChangeDeliveryFlowDescription>
        </Row>

        {!isMobile && onClose && (
          <CustomTouchableOpacity
            id="close-quick"
            data-cta-name-en="Close"
            data-cta-name-th="ปิด"
            data-cta-type="Button"
            onClick={() => onClose()}
          >
            <CustomImg src={CloseIcon} alt="close" />
          </CustomTouchableOpacity>
        )}

        {!isTabletOrMobile && !onClose && (
          <CustomTouchableOpacity
            id="close-quick"
            data-cta-name-en="Close"
            data-cta-name-th="ปิด"
            data-cta-type="Button"
            onClick={() => onClose()}
          >
            <CustomImg src={CloseIcon} alt="close" />
          </CustomTouchableOpacity>
        )}
      </Header>
      {(isDesktopOrLaptop || isTablet) && (
        <Divider />
      )}
      <Container isDefaultModalSize={isDefaultModalSize}>{children}</Container>
    </div>
  );
}
